import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Package, Plus, Trash2, Bell, MessageCircle, CheckCircle2, XCircle, Inbox } from "lucide-react";
import { toast } from "sonner";
import { StatusesCard } from "@/components/StatusesCard";
import { DocTemplatesCard } from "@/components/DocTemplates";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [newProd, setNewProd] = useState("");
  const [loading, setLoading] = useState(true);
  const [imm, setImm] = useState(null);
  const [immSaving, setImmSaving] = useState(false);
  const [wa, setWa] = useState(null);
  const [waSaving, setWaSaving] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  const [inbox, setInbox] = useState(null);
  const loadInbox = () => api.get("/inbox/status").then((r) => setInbox(r.data)).catch(() => {});
  const runInbox = async () => {
    await api.post("/inbox/run");
    toast.success("Controllo casella avviato");
    setTimeout(loadInbox, 8000);
  };

  const load = () => api.get("/products").then((r) => { setProducts(r.data); setLoading(false); });
  useEffect(() => {
    load();
    api.get("/settings/immediate-statuses").then((r) => setImm(r.data)).catch(() => {});
    api.get("/settings/whatsapp-notify").then((r) => setWa(r.data)).catch(() => {});
    loadInbox();
  }, []);
  const toggleImm = (s) => setImm((d) => ({ ...d, immediate: d.immediate.includes(s) ? d.immediate.filter((x) => x !== s) : [...d.immediate, s] }));
  const saveImm = async () => {
    setImmSaving(true);
    try { await api.put("/settings/immediate-statuses", { values: imm.immediate }); toast.success("Impostazione salvata"); }
    catch { toast.error("Errore salvataggio"); } finally { setImmSaving(false); }
  };

  const setWaField = (k, v) => setWa((d) => ({ ...d, settings: { ...d.settings, [k]: v } }));
  const toggleWaStatus = (s) => setWaField("statuses", wa.settings.statuses.includes(s) ? wa.settings.statuses.filter((x) => x !== s) : [...wa.settings.statuses, s]);
  const toggleWaRecipient = (r) => setWaField("recipients", wa.settings.recipients.includes(r) ? wa.settings.recipients.filter((x) => x !== r) : [...wa.settings.recipients, r]);
  const saveWa = async () => {
    setWaSaving(true);
    try { await api.put("/settings/whatsapp-notify", wa.settings); toast.success("Impostazione WhatsApp salvata"); }
    catch { toast.error("Errore salvataggio"); } finally { setWaSaving(false); }
  };
  const sendTest = async () => {
    if (!testPhone.trim()) { toast.error("Inserisci un numero di test"); return; }
    setTesting(true);
    try { const { data } = await api.post("/settings/whatsapp-test", { phone: testPhone }); toast.success(`WhatsApp di test inviato (id ${data.message_id})`); }
    catch (e) { toast.error(e.response?.data?.detail || "Invio test fallito"); } finally { setTesting(false); }
  };

  const persist = async (vals) => {
    try { const { data } = await api.put("/products", { products: vals }); setProducts(data.products); return true; }
    catch { toast.error("Errore nel salvataggio"); return false; }
  };

  const add = async () => {
    const v = newProd.trim();
    if (!v) return;
    if (products.includes(v)) { toast.error("Prodotto già presente"); return; }
    if (await persist([...products, v])) { setNewProd(""); toast.success("Prodotto aggiunto"); }
  };

  const remove = async (p) => {
    if (await persist(products.filter((x) => x !== p))) toast.success("Prodotto rimosso");
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="products-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Prodotti Finanziari</h1>
        <p className="text-sm text-muted-foreground mt-1">Gestisci i prodotti selezionabili nella creazione delle pratiche</p>
      </div>
      <Card className="p-5 space-y-4">
        <div className="flex gap-2">
          <Input data-testid="new-product-input" placeholder="Nome nuovo prodotto…" value={newProd} onChange={(e) => setNewProd(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button data-testid="add-product-btn" onClick={add}><Plus className="h-4 w-4 mr-2" />Aggiungi</Button>
        </div>
        <div className="space-y-2" data-testid="products-list">
          {products.map((p) => (
            <div key={p} data-testid={`product-item-${p}`} className="flex items-center gap-3 rounded-lg border border-border p-3 hover:bg-accent/40">
              <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/10 text-primary shrink-0"><Package className="h-4 w-4" /></div>
              <span className="flex-1 text-sm font-medium">{p}</span>
              <Button variant="ghost" size="icon" data-testid={`delete-product-${p}`} onClick={() => remove(p)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          ))}
          {!loading && products.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Nessun prodotto configurato</p>}
        </div>
      </Card>

      <StatusesCard />
      <DocTemplatesCard />

      {imm && (
        <Card className="p-5" data-testid="immediate-statuses-card">
          <div className="flex items-center gap-2 mb-1"><Bell className="h-4 w-4 text-primary" /><h2 className="font-heading font-semibold">Notifiche immediate al cambio stato</h2></div>
          <p className="text-xs text-muted-foreground mb-3">Per gli stati selezionati l'email ai soggetti coinvolti parte <strong>subito</strong> (gli altri restano nel digest orario).</p>
          <div className="flex flex-wrap gap-2">
            {imm.statuses.map((s) => (
              <label key={s} className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm cursor-pointer hover:bg-accent" data-testid={`imm-status-${s}`}>
                <Checkbox checked={imm.immediate.includes(s)} onCheckedChange={() => toggleImm(s)} />{s}
              </label>
            ))}
          </div>
          <div className="mt-3"><Button size="sm" data-testid="imm-status-save" onClick={saveImm} disabled={immSaving}>{immSaving ? "Salvataggio…" : "Salva impostazione"}</Button></div>
        </Card>
      )}

      {wa && (
        <Card className="p-5" data-testid="whatsapp-notify-card">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2"><MessageCircle className="h-4 w-4 text-primary" /><h2 className="font-heading font-semibold">Notifiche WhatsApp al cambio stato</h2></div>
            {wa.connection?.connected
              ? <span className="flex items-center gap-1 text-xs text-green-600" data-testid="wa-conn-ok"><CheckCircle2 className="h-3.5 w-3.5" />Numero collegato{wa.connection.name ? `: ${wa.connection.name}` : ""}</span>
              : <span className="flex items-center gap-1 text-xs text-red-500" data-testid="wa-conn-ko"><XCircle className="h-3.5 w-3.5" />Nessun numero WhatsApp collegato su Zernio</span>}
          </div>
          <p className="text-xs text-muted-foreground mb-3">In aggiunta all'email, invia un messaggio WhatsApp al <strong>convenzionato/segnalatore</strong> collegato quando la pratica passa a uno degli stati selezionati. Richiede un template approvato su Zernio/Meta.</p>

          <label className="flex items-center gap-2 text-sm mb-3 cursor-pointer" data-testid="wa-enabled-toggle">
            <Checkbox checked={!!wa.settings.enabled} onCheckedChange={(v) => setWaField("enabled", !!v)} />
            <span className="font-medium">Abilita notifiche WhatsApp</span>
          </label>

          <p className="text-xs font-medium text-muted-foreground mb-1">Invia per questi stati:</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {wa.statuses.map((s) => (
              <label key={s} className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm cursor-pointer hover:bg-accent" data-testid={`wa-status-${s}`}>
                <Checkbox checked={wa.settings.statuses.includes(s)} onCheckedChange={() => toggleWaStatus(s)} />{s}
              </label>
            ))}
          </div>

          <p className="text-xs font-medium text-muted-foreground mb-1">Destinatari:</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {["convenzionato", "segnalatore"].map((r) => (
              <label key={r} className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm cursor-pointer hover:bg-accent capitalize" data-testid={`wa-recipient-${r}`}>
                <Checkbox checked={wa.settings.recipients.includes(r)} onCheckedChange={() => toggleWaRecipient(r)} />{r}
              </label>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Nome template</label>
              <Input value={wa.settings.template_name} onChange={(e) => setWaField("template_name", e.target.value)} data-testid="wa-template-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Lingua template</label>
              <Input value={wa.settings.template_language} onChange={(e) => setWaField("template_language", e.target.value)} data-testid="wa-template-lang" />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mb-3">Variabili template, in ordine: {"{{1}}"} = nome partner, {"{{2}}"} = numero pratica istituto, {"{{3}}"} = nome cliente, {"{{4}}"} = nuovo stato.</p>

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" data-testid="wa-save" onClick={saveWa} disabled={waSaving}>{waSaving ? "Salvataggio…" : "Salva impostazione"}</Button>
            <div className="flex items-center gap-2 ml-auto">
              <Input placeholder="Numero test (es. 3331234567)" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} className="h-9 w-48" data-testid="wa-test-phone" />
              <Button size="sm" variant="outline" data-testid="wa-test-send" onClick={sendTest} disabled={testing}>{testing ? "Invio…" : "Invia test"}</Button>
            </div>
          </div>
        </Card>
      )}
      {inbox && <InboxCard inbox={inbox} onRun={runInbox} onRefresh={loadInbox} />}
    </div>
  );
}

const STATUS_CLS = { creata: "text-emerald-600", unita: "text-sky-600", rifiutata: "text-amber-600", errore: "text-red-500" };

function InboxCard({ inbox, onRun, onRefresh }) {
  return (
    <Card className="p-5 mt-6" data-testid="inbox-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2"><Inbox className="h-4 w-4 text-primary" /><h2 className="font-heading font-semibold">Pratiche da email</h2></div>
        {inbox.configured
          ? <span className="flex items-center gap-1 text-xs text-emerald-600" data-testid="inbox-conn-ok"><CheckCircle2 className="h-3.5 w-3.5" />{inbox.address}</span>
          : <span className="flex items-center gap-1 text-xs text-red-500" data-testid="inbox-conn-ko"><XCircle className="h-3.5 w-3.5" />Casella non configurata</span>}
      </div>
      <p className="text-xs text-muted-foreground mb-3">Gli utenti registrati inoltrano un'email alla casella dedicata: ogni 15 minuti il gestionale crea la pratica (dati letti con AI dal testo e dagli allegati), la assegna al mittente e allega i file, scartando loghi e immagini della firma. Se lo stesso mittente invia entro 48 ore un'altra email per lo stesso cliente (stesso nome), allegati e testo vengono uniti alla pratica già creata. Le email da mittenti non registrati vengono ignorate.</p>
      {inbox.configured && !inbox.enabled && <p className="text-xs text-amber-600 mb-3" data-testid="inbox-disabled">Lettura automatica spenta: per attivarla imposta la chiave INBOX_POLL_ENABLED = true nei Secrets dell'app pubblicata e ripubblica.</p>}
      <div className="flex gap-2 mb-3">
        <Button size="sm" data-testid="inbox-run" onClick={onRun} disabled={!inbox.configured || !inbox.enabled}>Controlla ora</Button>
        <Button size="sm" variant="outline" data-testid="inbox-refresh" onClick={onRefresh}>Aggiorna elenco</Button>
      </div>
      <div className="divide-y text-sm" data-testid="inbox-log">
        {inbox.log.length === 0 && <p className="text-xs text-muted-foreground py-2">Nessuna email elaborata.</p>}
        {inbox.log.map((l, i) => (
          <div key={i} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`inbox-log-${i}`}>
            <span className={`text-xs font-semibold uppercase ${STATUS_CLS[l.status] || ""}`}>{l.status}</span>
            <span className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString("it-IT")}</span>
            <span className="truncate max-w-[16rem]">{l.subject || "—"}</span>
            <span className="text-xs text-muted-foreground">{l.from}</span>
            {l.practice_id && <a className="text-xs text-primary underline" href={`/pratiche/${l.practice_id}`}>{l.numero} · {l.documenti} doc{l.firme_scartate ? ` · ${l.firme_scartate} immagini firma scartate` : ""}</a>}
            {l.error && <span className="text-xs text-red-500">{l.error}</span>}
          </div>
        ))}
      </div>
    </Card>
  );
}
